import type { HumanoidAppearance } from '../GhostRenderer';
import { npc } from './npc';

/**
 * Tidewend's vault and training quarter (content/zones/tidewend.toml).
 * Dedicated specialists per class: keys and a ledger, a shield, a warhammer,
 * a spellbook, a holy candle, a shadow crystal staff, a bow, an oak cane,
 * a hood and daggers, and a storm totem. Lamp-lit like the rest of the village.
 */
export const TIDEWEND_TRAINERS: Record<string, HumanoidAppearance> = {
  // ── Vaultkeeper Barnaby — "secure under our lock and key" ──
  banker_barnaby: npc({
    shape: 'round',
    color: '#d9b08c',
    eyes: 'round',
    mouth: 'smile',
    humanoid: {
      hair: { style: 'thin', color: '#9a9488' },
      beard: { style: 'short', color: '#9a9488' },
      garment: { kind: 'tunic', color: '#2c3e5c', trimColor: '#c8a040', beltColor: '#3a2a1c' }, // vault-keeper's coat
      props: [
        { kind: 'key_ring', color: '#c8a040' },
        { kind: 'book', color: '#5a2e2a' }, // the ledger
      ],
      art: { light: 'lamp', role: 'banker' },
    },
  }),

  // ── Captain Elowen — warriors; "discipline is quiet" ──
  captain_elowen: npc({
    shape: 'wide',
    color: '#c99a74',
    eyes: 'round',
    mouth: 'neutral',
    humanoid: {
      hair: { style: 'short', color: '#a8542e' }, // copper crop
      cloak: { color: '#7a2a2a', claspColor: '#d8b050' }, // crimson captain's cloak
      garment: { kind: 'tunic', color: '#8a96a6', trimColor: '#4a5260', beltColor: '#3a2a1c' }, // steel surcoat
      props: [{ kind: 'shield', color: '#9aa6b4', accentColor: '#d8b050' }],
      art: { light: 'lamp', role: 'trainer' },
    },
  }),

  // ── Justiciar Kareth — paladins; "walk in the Light" ──
  justiciar_kareth: npc({
    shape: 'classic',
    color: '#eed2b2',
    eyes: 'round',
    mouth: 'neutral',
    humanoid: {
      hair: { style: 'short', color: '#c49a45' },
      cloak: { color: '#2c3e6b', claspColor: '#f0c040' },
      garment: { kind: 'tunic', color: '#d8dce2', trimColor: '#c8a030', beltColor: '#4a3a28' },
      props: [{ kind: 'hammer', color: '#8a6a3a', headColor: '#d4af37' }],
      art: { light: 'lamp', role: 'trainer' },
    },
  }),

  // ── Arcanist Vale — mages; the lamps bend toward the Coil ──
  arcanist_vale: npc({
    shape: 'slim',
    color: '#e0c8b0',
    eyes: 'round',
    mouth: 'neutral',
    humanoid: {
      hair: { style: 'loose', color: '#2a2440', streakColor: '#8be9fd' },
      garment: { kind: 'robe', color: '#3a3a7a', trimColor: '#c8a8ff', underColor: '#d8d0f0' },
      props: [{ kind: 'book', color: '#4a2a6a', glowColor: '#8be9fd' }], // spellbook, runes lit
      art: { light: 'lamp', role: 'trainer' },
    },
  }),

  // ── Sister Vael — priests; "a steady prayer" ──
  sister_vael: npc({
    shape: 'slim',
    color: '#ebd5c5',
    eyes: 'round',
    mouth: 'smile',
    humanoid: {
      headgear: { kind: 'hood', color: '#f0ece1', accentColor: '#d4af37' },
      cloak: { color: '#4a3a6a', claspColor: '#e0c060' },
      garment: { kind: 'robe', color: '#f8f4ea', trimColor: '#c8a850', underColor: '#eae0d0' },
      props: [{ kind: 'candle', color: '#e8e0d0', flameColor: '#fff5aa' }],
      art: { light: 'lamp', role: 'trainer' },
    },
  }),

  // ── Hexbinder Kane — warlocks; "the shadows in Tidewend have ears" ──
  hexbinder_kane: npc({
    shape: 'slim',
    color: '#98849c',
    eyes: 'round',
    mouth: 'neutral',
    humanoid: {
      hair: { style: 'loose', color: '#181224' },
      cloak: { color: '#2b1b3d', claspColor: '#9333ea' },
      garment: { kind: 'robe', color: '#1e1428', trimColor: '#7e22ce', underColor: '#2e1045' },
      props: [{ kind: 'crystal_staff', color: '#2a1a3a', crystalColor: '#a855f7' }],
      art: { light: 'lamp', role: 'trainer' },
    },
  }),

  // ── Warden Rowan — hunters; "read what the wild refuses to say" ──
  warden_rowan: npc({
    shape: 'classic',
    color: '#b8865e',
    eyes: 'round',
    mouth: 'smile',
    humanoid: {
      hair: { style: 'short', color: '#4a3a20' },
      beard: { style: 'stubble', color: '#4a3a20' },
      headgear: { kind: 'leaf_crown', color: '#5a8a3a', accentColor: '#c8483a' },
      cloak: { color: '#3e5a34', claspColor: '#8a6a3a' },
      garment: { kind: 'tunic', color: '#7a5a3a', trimColor: '#4a3a28' },
      props: [{ kind: 'bow', color: '#7a5230' }],
      art: { light: 'lamp', role: 'trainer' },
    },
  }),

  // ── Druid Elora — druids; "the roots run deeper than the harbor pilings" ──
  druid_elora: npc({
    shape: 'classic',
    color: '#a47854',
    eyes: 'round',
    mouth: 'smile',
    humanoid: {
      headgear: { kind: 'leaf_crown', color: '#4a7c36', accentColor: '#88c057' },
      cloak: { color: '#2a482d', claspColor: '#b8a060' },
      garment: { kind: 'tunic', color: '#5c4028', trimColor: '#3d6b35', beltColor: '#3a2a1a' },
      props: [{ kind: 'cane', color: '#684828' }],
      art: { light: 'lamp', role: 'trainer' },
    },
  }),

  // ── Whisper Tamsin — rogues; "if you saw me before I spoke, I was being polite" ──
  whisper_tamsin: npc({
    shape: 'slim',
    color: '#c8a890',
    eyes: 'round',
    mouth: 'neutral',
    humanoid: {
      headgear: { kind: 'hood', color: '#2a2230', accentColor: '#6a3a5a' },
      garment: { kind: 'tunic', color: '#3a3040', trimColor: '#1e1824', beltColor: '#2a1e1a' },
      props: [
        { kind: 'scarf', color: '#5a2a4a', stripeColor: '#8a4a6a' },
        { kind: 'daggers', color: '#b8c0c8' },
      ],
      art: { light: 'lamp', role: 'trainer' },
    },
  }),

  // ── Stormspeaker Iona — shamans; "the tide keeps three rhythms tonight" ──
  stormspeaker_iona: npc({
    shape: 'round',
    color: '#9a6a4a',
    eyes: 'round',
    mouth: 'smile',
    humanoid: {
      hair: { style: 'loose', color: '#1a2a3a', streakColor: '#7fd8ff' },
      garment: { kind: 'tunic', color: '#2a6a7a', trimColor: '#e0c070', beltColor: '#4a3a28' },
      props: [{ kind: 'totem', color: '#8a6a4a', featherColor: '#e8e0d0', glintColor: '#9fe8ff' }],
      art: { light: 'lamp', role: 'trainer' },
    },
  }),
};
